import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GSTINVerificationInput } from "@/components/common/GSTINVerificationInput";
import { GSTVerificationResult } from "@/types/gst";
import { extractFromGSTIN } from "@/lib/onboarding/gstUtils";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Puducherry", "Chandigarh",
];

const GST_RATES = [0, 5, 12, 18, 28];

const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const [companyName, setCompanyName] = useState("");
  const [gstin, setGstin] = useState("");
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");

  // GST settings (stored locally for now)
  const [gstEnabled, setGstEnabled] = useState(true);
  const [defaultGstRate, setDefaultGstRate] = useState("18");
  const [compositionScheme, setCompositionScheme] = useState(false);
  const [eInvoicing, setEInvoicing] = useState(false);
  const [eWayBillThreshold, setEWayBillThreshold] = useState("50000");
  const [reverseCharge, setReverseCharge] = useState(false);
  const [hsnMandatory, setHsnMandatory] = useState(true);

  // Fetch company data from database on mount
  useEffect(() => {
    if (!user?.company) {
      setLoading(false);
      return;
    }

    const fetchCompany = async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", user.company as string)
        .single();

      if (error) {
        console.error("Failed to fetch company:", error);
        setLoading(false);
        return;
      }

      if (data) {
        setCompanyId(data.id);
        setCompanyName(data.name || "");
        setGstin(data.gstin || "");
        setPanNumber(data.pan || "");
        setEmail(data.email || "");
        setPhone(data.phone || "");
        setAddress(data.address || "");
        setState(data.state || "");
        setCity(data.city || "");
        setPincode(data.pincode || "");
      }
      setLoading(false);
    };

    fetchCompany();
  }, [user?.company]);

  const handleApplyGstVerification = (data: GSTVerificationResult) => {
    setGstin(data.gstin);
    if (data.tradeName || data.legalName) {
      setCompanyName(data.tradeName || data.legalName || companyName);
    }
    if (data.pan) {
      setPanNumber(data.pan);
    }
    if (data.address) {
      setAddress(data.address);
    }
    if (data.city) {
      setCity(data.city);
    }
    if (data.state) {
      setState(data.state);
    }
    if (data.pincode) {
      setPincode(data.pincode);
    }
    if (data.compositionScheme !== undefined) {
      setCompositionScheme(data.compositionScheme);
    }
    setGstEnabled(data.status === "Active");
  };

  const handleSave = async () => {
    if (!companyId) {
      toast.error("No company found to update");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("companies")
        .update({
          name: companyName.trim(),
          gstin: gstin.trim() || null,
          pan: panNumber.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          address: address.trim() || null,
          state: state || null,
          city: city.trim() || null,
          pincode: pincode.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", companyId);

      if (error) throw error;
      toast.success("Settings saved successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage company profile and GST configuration</p>
      </div>

      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company">Company Profile</TabsTrigger>
          <TabsTrigger value="gst">GST Configuration</TabsTrigger>
          <TabsTrigger value="invoicing">Invoicing</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="space-y-4 mt-4">
          <SettingField label="Company Name" value={companyName} onChange={setCompanyName} />
          <SettingField label="PAN Number" value={panNumber} onChange={setPanNumber} placeholder="AAAAA9999A" />
          <SettingField label="Email" value={email} onChange={setEmail} type="email" />
          <SettingField label="Phone" value={phone} onChange={setPhone} />
          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">State</label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger className="h-9 text-sm border-0 p-0 bg-transparent shadow-none"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent>
                {INDIAN_STATES.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <SettingField label="City" value={city} onChange={setCity} />
          <SettingField label="Pincode" value={pincode} onChange={setPincode} />
          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full bg-transparent text-sm text-foreground outline-none resize-none"
              rows={2}
            />
          </div>
        </TabsContent>

        <TabsContent value="gst" className="space-y-4 mt-4">
          <div className="p-4 rounded-lg bg-accent/5 border border-accent/20 text-sm">
            <h3 className="font-semibold text-foreground mb-1">GST Registration Details</h3>
            <p className="text-muted-foreground">Configure your GST settings for compliance and auto-calculation on invoices.</p>
          </div>

          <div className="p-4 rounded-lg bg-card border border-border">
            <GSTINVerificationInput
              value={gstin}
              onChange={(val) => {
                const extracted = extractFromGSTIN(val);
                setGstin(val);
                if (extracted.pan && !panNumber) setPanNumber(extracted.pan);
                if (extracted.state?.name && !state) setState(extracted.state.name);
              }}
              onApplyVerification={handleApplyGstVerification}
              currentFormValues={{
                name: companyName,
                legalName: companyName,
                pan: panNumber,
                address,
                city,
                state,
                pincode,
                compositionScheme,
              }}
              isEditMode={true}
              label="GSTIN (15-Digit Tax Identification Number)"
            />
          </div>

          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Default GST Rate</label>
            <Select value={defaultGstRate} onValueChange={setDefaultGstRate}>
              <SelectTrigger className="h-9 text-sm border-0 p-0 bg-transparent shadow-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GST_RATES.map((r) => <SelectItem key={r} value={String(r)} className="text-sm">{r}%</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ToggleSetting label="GST Enabled" description="Enable GST calculation on all invoices" checked={gstEnabled} onChange={setGstEnabled} />
          <ToggleSetting label="Composition Scheme" description="Under composition scheme — simplified compliance, no ITC" checked={compositionScheme} onChange={setCompositionScheme} />
          <ToggleSetting label="Reverse Charge Mechanism" description="Enable RCM for applicable purchases" checked={reverseCharge} onChange={setReverseCharge} />
          <ToggleSetting label="HSN Code Mandatory" description="Require HSN/SAC code on all invoice items" checked={hsnMandatory} onChange={setHsnMandatory} />
          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">E-Way Bill Threshold (₹)</label>
            <input
              type="number"
              value={eWayBillThreshold}
              onChange={(e) => setEWayBillThreshold(e.target.value)}
              className="w-full bg-transparent text-sm text-foreground outline-none"
            />
            <p className="text-xs text-muted-foreground mt-1">E-Way bill auto-generated for shipments above this value</p>
          </div>
        </TabsContent>

        <TabsContent value="invoicing" className="space-y-4 mt-4">
          <ToggleSetting label="E-Invoicing" description="Enable e-invoicing via GST portal (mandatory for turnover > ₹5 Cr)" checked={eInvoicing} onChange={setEInvoicing} />
          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Invoice Prefix</label>
            <input type="text" defaultValue="INV-" className="w-full bg-transparent text-sm text-foreground outline-none" />
          </div>
          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Terms & Conditions (Default)</label>
            <textarea
              defaultValue="Goods once sold will not be taken back. Payment due within 30 days."
              className="w-full bg-transparent text-sm text-foreground outline-none resize-none"
              rows={3}
            />
          </div>
          <div className="p-4 rounded-lg bg-card border border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Bank Details for Invoice</label>
            <div className="space-y-2 text-sm">
              <input defaultValue="" className="w-full bg-transparent text-foreground outline-none" placeholder="Bank Name" />
              <input defaultValue="" className="w-full bg-transparent text-foreground outline-none" placeholder="Account Number" />
              <input defaultValue="" className="w-full bg-transparent text-foreground outline-none" placeholder="IFSC Code" />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Button onClick={handleSave} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90">
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );
};

function SettingField({ label, value, onChange, type = "text", placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div className="p-4 rounded-lg bg-card border border-border">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-sm text-foreground outline-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function ToggleSetting({ label, description, checked, onChange }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="p-4 rounded-lg bg-card border border-border flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-accent" : "bg-muted-foreground/30"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}

export default SettingsPage;
